import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppScreen } from 'components/ds/AppShell';
import { Button } from 'components/ds/Button';
import { MeetingRepository } from 'repository/meetingRepository';
import { isOkResponse, errorMessageOf } from 'common/utils/apiResponse';
import { useAlert } from 'contexts/AlertContext';
import { isNativeApp, openNativePlaceSearch } from 'common/webview';
import styles from './PlaceVote.module.css';

/**
 * 장소 후보 투표 — 모임을 모으는 단계.
 *
 * 방장이 후보를 올리고 참여자가 투표합니다. 모임이 시작하면 투표는 닫히고,
 * 그 뒤로는 장소를 누구나 바로 바꿉니다(그때는 변경 이력이 남습니다).
 *
 * 시간 투표(/schedule/vote)와 따로 둡니다 — 묶으면 "시간은 좋은데 장소는 싫다"
 * 를 표현할 수 없고 후보 수가 곱으로 늘어납니다.
 *
 * 후보가 없을 때도 막다른 화면이 되지 않게 합니다. 방장에게는 올릴 자리를,
 * 참여자에게는 왜 비어 있는지를 알려줍니다 — 예전 일정 투표 화면이 "제안된
 * 일정이 없습니다" 만 띄워 사용자가 무엇을 해야 하는지 몰랐습니다.
 */

type Candidate = {
    candidateId: string;
    placeName: string;
    placeAddress?: string | null;
    memo?: string | null;
    voteCount: number;
    votedByMe: boolean;
};

type Board = {
    candidates: Candidate[];
    voterCount: number;
    votingOpen: boolean;
    isHost: boolean;
};

const IcCheck = ({ size = 16 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6L9 17l-5-5" />
    </svg>
);

const IcTrash = ({ size = 15 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
    </svg>
);

export const PlaceVote = () => {
    const { meetingId } = useParams<{ meetingId: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showAlert, showConfirm } = useAlert();

    const [board, setBoard] = useState<Board | null>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [failed, setFailed] = useState(false);

    /* 방장이 후보를 직접 적는 자리 — 앱에서는 지도에서 고를 수 있습니다 */
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ placeName: '', placeAddress: '', memo: '' });

    const load = useCallback(async () => {
        if (!meetingId) return;
        setLoading(true);
        try {
            const res: any = await MeetingRepository.onGetPlaceCandidates(meetingId);
            /* onRequest 는 예외를 던지지 않습니다 — 상태를 봐야 실패를 압니다 */
            if (!isOkResponse(res)) {
                setFailed(true);
                return;
            }
            setBoard(res?.data?.data || null);
            setFailed(false);
        } finally {
            setLoading(false);
        }
    }, [meetingId]);

    useEffect(() => { void load(); }, [load]);

    const toggleVote = useCallback(async (c: Candidate) => {
        if (!meetingId || busy) return;
        setBusy(true);
        try {
            const res: any = await MeetingRepository.onVotePlaceCandidate(
                meetingId, c.candidateId, !c.votedByMe);
            if (!isOkResponse(res)) {
                await showAlert({ message: errorMessageOf(res) || t('placeVote.voteFail') });
                return;
            }
            setBoard(res?.data?.data || board);
        } finally {
            setBusy(false);
        }
    }, [meetingId, busy, board, showAlert, t]);

    /* 앱에서는 지도 검색으로 고르고, 웹에서는 직접 적습니다 */
    const pickFromMap = useCallback(async () => {
        if (!meetingId) return;
        const place = await openNativePlaceSearch();
        if (!place) return;
        setBusy(true);
        try {
            const res: any = await MeetingRepository.onPostPlaceCandidate(meetingId, {
                placeName: place.name,
                placeAddress: place.address,
                latitude: place.lat,
                longitude: place.lng,
            });
            if (!isOkResponse(res)) {
                await showAlert({ message: errorMessageOf(res) || t('placeVote.addFail') });
                return;
            }
            await load();
        } finally {
            setBusy(false);
        }
    }, [meetingId, showAlert, t, load]);

    const submitForm = useCallback(async () => {
        if (!meetingId || busy) return;
        const name = form.placeName.trim();
        if (!name) {
            await showAlert({ message: t('placeVote.enterName') });
            return;
        }
        setBusy(true);
        try {
            const res: any = await MeetingRepository.onPostPlaceCandidate(meetingId, {
                placeName: name,
                placeAddress: form.placeAddress.trim() || undefined,
                memo: form.memo.trim() || undefined,
            });
            if (!isOkResponse(res)) {
                await showAlert({ message: errorMessageOf(res) || t('placeVote.addFail') });
                return;
            }
            setForm({ placeName: '', placeAddress: '', memo: '' });
            setAdding(false);
            await load();
        } finally {
            setBusy(false);
        }
    }, [meetingId, busy, form, showAlert, t, load]);

    const removeCandidate = useCallback(async (c: Candidate) => {
        if (!meetingId || busy) return;
        const ok = await showConfirm({
            message: t('placeVote.confirmDelete', { name: c.placeName }),
        });
        if (!ok) return;
        setBusy(true);
        try {
            const res: any = await MeetingRepository.onDeletePlaceCandidate(meetingId, c.candidateId);
            if (!isOkResponse(res)) {
                await showAlert({ message: errorMessageOf(res) || t('placeVote.deleteFail') });
                return;
            }
            await load();
        } finally {
            setBusy(false);
        }
    }, [meetingId, busy, showConfirm, showAlert, t, load]);

    const confirmCandidate = useCallback(async (c: Candidate) => {
        if (!meetingId || busy) return;
        const ok = await showConfirm({
            message: t('placeVote.confirmPick', { name: c.placeName }),
        });
        if (!ok) return;
        setBusy(true);
        try {
            const res: any = await MeetingRepository.onConfirmPlaceCandidate(meetingId, c.candidateId);
            if (!isOkResponse(res)) {
                await showAlert({ message: errorMessageOf(res) || t('placeVote.confirmFail') });
                return;
            }
            await showAlert({ message: t('placeVote.confirmed', { name: c.placeName }) });
            navigate(`/meeting/detail/${meetingId}`);
        } finally {
            setBusy(false);
        }
    }, [meetingId, busy, showConfirm, showAlert, t, navigate]);

    if (loading) {
        return (
            <AppScreen title={t('placeVote.title')} bar="sub">
                <p className={styles.state}>{t('common.loading')}</p>
            </AppScreen>
        );
    }

    if (failed || !board) {
        return (
            <AppScreen title={t('placeVote.title')} bar="sub">
                <p className={styles.state}>{t('placeVote.loadFail')}</p>
            </AppScreen>
        );
    }

    const { candidates, voterCount, votingOpen, isHost } = board;

    return (
        <AppScreen title={t('placeVote.title')} bar="sub">
            <div className={styles.head}>
                <p className={styles.guide}>
                    {votingOpen
                        ? isHost ? t('placeVote.guideHost') : t('placeVote.guideMember')
                        : t('placeVote.closed')}
                </p>
                {candidates.length > 0 ? (
                    <p className={styles.count}>{t('placeVote.voterCount', { count: voterCount })}</p>
                ) : null}
            </div>

            {candidates.length === 0 ? (
                <p className={styles.state}>
                    {isHost ? t('placeVote.emptyHost') : t('placeVote.emptyMember')}
                </p>
            ) : (
                <ul className={styles.list}>
                    {candidates.map((c) => (
                        <li key={c.candidateId} className={styles.item}>
                            <button
                                type="button"
                                className={`${styles.pick} ${c.votedByMe ? styles.picked : ''}`}
                                disabled={busy || !votingOpen}
                                onClick={() => toggleVote(c)}
                                aria-pressed={c.votedByMe}
                            >
                                <span className={styles.box} aria-hidden>
                                    {c.votedByMe ? <IcCheck /> : null}
                                </span>
                                <span className={styles.info}>
                                    <span className={styles.name}>{c.placeName}</span>
                                    {c.placeAddress ? (
                                        <span className={styles.addr}>{c.placeAddress}</span>
                                    ) : null}
                                    {c.memo ? <span className={styles.memo}>{c.memo}</span> : null}
                                </span>
                                <span className={styles.votes}>
                                    {t('placeVote.votes', { count: c.voteCount })}
                                </span>
                            </button>

                            {isHost && votingOpen ? (
                                <div className={styles.hostRow}>
                                    <button type="button" className={styles.hostBtn}
                                        disabled={busy} onClick={() => confirmCandidate(c)}>
                                        {t('placeVote.pickThis')}
                                    </button>
                                    <button type="button" className={styles.hostIconBtn}
                                        disabled={busy} onClick={() => removeCandidate(c)}
                                        aria-label={t('placeVote.delete')}>
                                        <IcTrash />
                                    </button>
                                </div>
                            ) : null}
                        </li>
                    ))}
                </ul>
            )}

            {isHost && votingOpen ? (
                <div className={styles.add}>
                    {adding ? (
                        <>
                            <input
                                className={styles.input}
                                placeholder={t('placeVote.namePlaceholder')}
                                value={form.placeName}
                                onChange={(e) => setForm({ ...form, placeName: e.target.value })}
                                maxLength={200}
                            />
                            <input
                                className={styles.input}
                                placeholder={t('placeVote.addressPlaceholder')}
                                value={form.placeAddress}
                                onChange={(e) => setForm({ ...form, placeAddress: e.target.value })}
                                maxLength={500}
                            />
                            <input
                                className={styles.input}
                                placeholder={t('placeVote.memoPlaceholder')}
                                value={form.memo}
                                onChange={(e) => setForm({ ...form, memo: e.target.value })}
                                maxLength={200}
                            />
                            <div className={styles.addActions}>
                                <Button variant="primary" size="md" disabled={busy} onClick={submitForm}>
                                    {t('placeVote.addSubmit')}
                                </Button>
                                <Button variant="secondary" size="md" onClick={() => setAdding(false)}>
                                    {t('placeVote.cancel')}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <div className={styles.addActions}>
                            {/* 앱에서는 지도에서 고르는 게 훨씬 빠릅니다 — 좌표까지 함께 옵니다 */}
                            {isNativeApp() ? (
                                <Button variant="primary" size="md" disabled={busy} onClick={pickFromMap}>
                                    {t('placeVote.pickFromMap')}
                                </Button>
                            ) : null}
                            <Button variant="secondary" size="md" onClick={() => setAdding(true)}>
                                {t('placeVote.addByHand')}
                            </Button>
                        </div>
                    )}
                </div>
            ) : null}
        </AppScreen>
    );
};
